/**
 * BrandResolver — sits INSIDE the BrowserRouter so it can use
 * `useLocation`. Watches `location.pathname`, extracts the tenant
 * slug, fetches brand overrides via the public API, and applies them
 * via the brand registry. One slug = one network call (cached on the
 * brand cache key); navigating BETWEEN PAGES OF THE SAME TENANT
 * doesn't refetch.
 *
 * Resolution order (first non-null wins):
 *   1. URL path slug    → /public/brand-from-tenant
 *   2. Host             → /public/brand-from-host (DNS / vanity)
 *   3. Default brand
 *
 * Replaces the old 6-source query-string-era resolver that lived in
 * App.tsx (preset slugs `#/_/m2`, `?brand=`, hash query `?tenant=`,
 * tenant slug `#/_t/`, host, post-auth user.brand_overrides + URL
 * precedence guard). The path slug IS the brand signal; everything
 * else falls out.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { brand, combinePresetAndOverrides, setActiveBrand, setActiveCompanyTenantCode } from '@/brand';
import { applyBrandCSS } from '@/brand/apply-brand';
import { documentTitleFor } from '@/brand/documentTitle';
import type { BrandOverrides } from '@/brand/types';
import api from '@/services/api';
import { readTenantFromPathname } from './useActiveTenant';
import { cacheTransactionalEmailForSlug } from './tenantResolver';
import { isPublicLegalPath } from '@modules/legal/publicPaths';
import { AUTH_CHANGE_EVENT } from '@modules/auth/services/authService';

type BrandResolution = BrandOverrides | null;

// v5 (2026-10-28): a v4 entry was written before brands carried `agentAvatars`.
// Painting it would draw an M² Team tenant's agents as circles until the fresh
// fetch lands, so v4 is never read again (and is swept below and on logout).
const OVERRIDES_CACHE_PREFIX = 'brand_overrides_v5:';

function applyBrand(overrides: BrandOverrides | null): void {
  setActiveBrand(overrides);
  applyBrandCSS(brand);
  try {
    // What the tab shows, by the one rule (documentTitle.ts) — the pre-paint script repeats it.
    window.localStorage.setItem(pageTitleCacheKey(), documentTitleFor(brand));
    if (overrides && Object.keys(overrides).length > 0) {
      window.localStorage.setItem(overridesCacheKey(), JSON.stringify(overrides));
    } else {
      window.localStorage.removeItem(overridesCacheKey());
    }
  } catch { /* ignore */ }
}

/**
 * Read the cached brand overrides for a specific tenant slug WITHOUT
 * touching the cacheScope() helper (which reads window.location.pathname
 * and would lie when we're called for a slug DIFFERENT from the
 * currently-rendered URL — e.g. during the brief window between the
 * navigation event and React Router committing). Returns null if no
 * cache entry exists or it's malformed.
 */
function readCachedOverridesForSlug(slug: string): BrandOverrides | null {
  try {
    const raw = window.localStorage.getItem(OVERRIDES_CACHE_PREFIX + 'tenant:' + slug);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BrandOverrides;
    if (parsed && Object.keys(parsed).length > 0) return parsed;
    return null;
  } catch {
    return null;
  }
}

/** Cache keys keyed by URL slug so each branded surface keeps its
 *  own cached brand. The inline `<script>` in `index.html` reads
 *  pageTitleCacheKey() to set the document title before React mounts. */
function cacheScope(): string {
  try {
    const slug = readTenantFromPathname(window.location.pathname);
    if (slug) return 'tenant:' + slug;
  } catch { /* ignore */ }
  return 'host:' + window.location.host;
}
// v2 (item 98): a v1 entry held brand.pageTitle, which could be the platform's
// name on a company host — the pre-paint script reads v2 only.
function pageTitleCacheKey(): string { return 'brand_page_title_v2:' + cacheScope(); }
function overridesCacheKey(): string { return OVERRIDES_CACHE_PREFIX + cacheScope(); }

/**
 * The authenticated user's OWN tenant brand, read straight from the auth
 * session (set at login from `user.brand_overrides` + `user.brand_id`).
 * This is the brand the super-admin configured for that tenant, so it is
 * authoritative for the logged-in user on ANY page that doesn't carry a
 * different tenant slug — and, crucially, it's always present after a
 * refresh (sessionStorage survives reload), so the brand never reverts to
 * default on reserved/root URLs (e.g. /super-admin, /design-system) or on
 * localhost where the host matches no tenant domain.
 */
function readAuthedUserBrand(): { overrides: BrandOverrides | null; companyCode: string | null } | null {
  try {
    const raw = window.sessionStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw) as {
      brand_id?: string | null;
      brand_overrides?: BrandOverrides | null;
      company_tenant_code?: string | null;
    };
    const overrides = combinePresetAndOverrides(
      user?.brand_id ?? null,
      user?.brand_overrides ?? null,
    );
    if (!overrides) return null;
    return { overrides, companyCode: user?.company_tenant_code ?? null };
  } catch {
    return null;
  }
}

/**
 * The authenticated user's OWN tenant slug, used to fetch their tenant
 * brand FRESH from the API on no-slug pages. We fetch (rather than reuse
 * the login-time session copy) because the session copy goes stale the
 * moment the super-admin edits the tenant brand — and the whole point is
 * that the brand must match what was just configured. Prefer the user's
 * own tenant_code; fall back to the company code.
 */
/** What, in the signed-in user, decides the brand this tab wears: their
 *  organization's name and brand. A change (a rename, a brand edit, seen by
 *  the next token check) means the brand must be fetched again. */
function authedBrandSignature(): string {
  try {
    const raw = window.sessionStorage.getItem('user');
    if (!raw) return '';
    const user = JSON.parse(raw) as { tenant_name?: string; brand_id?: string | null; brand_overrides?: unknown };
    return JSON.stringify([user?.tenant_name ?? null, user?.brand_id ?? null, user?.brand_overrides ?? null]);
  } catch {
    return '';
  }
}

function readAuthedTenantCode(): string | null {
  try {
    const raw = window.sessionStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw) as { tenant_code?: string | null; company_tenant_code?: string | null };
    return user?.tenant_code || user?.company_tenant_code || null;
  } catch {
    return null;
  }
}

/** Apply any previously-cached brand SYNCHRONOUSLY at module load so
 *  refreshing a branded URL doesn't flash the default theme. */
export function applyCachedBrandSync(): void {
  try {
    const raw = window.localStorage.getItem(overridesCacheKey());
    let applied = false;
    if (raw) {
      const cached = JSON.parse(raw) as BrandOverrides;
      if (cached && Object.keys(cached).length > 0) {
        setActiveBrand(cached);
        applyBrandCSS(brand);
        applied = true;
      }
    }
    // No URL-scoped cache for this surface yet (first visit, or a no-slug
    // page like /super-admin on localhost). For an authenticated user,
    // paint their own tenant's brand immediately so the refresh never
    // flashes — or sticks on — the default theme.
    if (!applied && !readTenantFromPathname(window.location.pathname)) {
      const authed = readAuthedUserBrand();
      if (authed) {
        setActiveBrand(authed.overrides);
        applyBrandCSS(brand);
      }
    }
    // Clean up legacy cache keys from earlier eras.
    window.localStorage.removeItem('brand_overrides_v2:host:' + window.location.host);
    window.localStorage.removeItem('brand_overrides_v3:host:' + window.location.host);
    window.localStorage.removeItem('brand_overrides_cache');
    window.localStorage.removeItem('login_tenant_code');
    // Also wipe stale per-tenant caches of earlier eras: v3 (the v3-era
    // resolver returned sparse overrides without merging in the preset, so
    // every entry paints a half-broken brand) and v4 (written before brands
    // carried agent avatar templates — see OVERRIDES_CACHE_PREFIX).
    try {
      const stale = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && (k.startsWith('brand_overrides_v3:') || k.startsWith('brand_overrides_v4:'))) stale.push(k);
      }
      stale.forEach((k) => window.localStorage.removeItem(k));
    } catch { /* ignore */ }
  } catch { /* ignore */ }
}

/**
 * Tagged result so the caller can distinguish:
 *   - 'ok' + overrides (possibly null when the tenant has no custom
 *     brand) → safe to apply, including a null-overrides fall-back
 *     to the default brand for a tenant that truly hasn't customized.
 *   - 'fetch_failed' → network/server hiccup; the caller MUST NOT
 *     wipe the currently-applied brand or it'll silently regress to
 *     the default mid-session.
 */
type TenantBrandFetch =
  | { kind: 'ok'; overrides: BrandOverrides | null }
  | { kind: 'fetch_failed' };

async function resolveBrandFromTenantSlug(slug: string): Promise<TenantBrandFetch> {
  try {
    const res = await api.get('/public/brand-from-tenant', { params: { tenant: slug } });
    setActiveCompanyTenantCode(slug);
    // Feed the transactional-email flag (already in this response) into the
    // shared cache so the login page doesn't re-fetch this same endpoint.
    cacheTransactionalEmailForSlug(slug, res.data?.transactional_email);
    return {
      kind: 'ok',
      overrides: combinePresetAndOverrides(
        res.data?.brand_id as string | null,
        (res.data?.brand_overrides as BrandOverrides | null) ?? null,
      ),
    };
  } catch {
    return { kind: 'fetch_failed' };
  }
}

async function resolveBrandFromHost(): Promise<BrandResolution> {
  try {
    const res = await api.get('/public/brand-from-host', {
      params: { host: window.location.host },
    });
    setActiveCompanyTenantCode(res.data?.company_tenant_code ?? null);
    return combinePresetAndOverrides(
      res.data?.brand_id as string | null,
      (res.data?.brand_overrides as BrandOverrides | null) ?? null,
    );
  } catch {
    return null;
  }
}

export function BrandResolver() {
  const location = useLocation();
  // The slug whose brand we have successfully APPLIED — used to
  // dedupe in-tenant navigations. We deliberately do NOT update this
  // until applyBrand runs. Otherwise React StrictMode's mount →
  // unmount → remount cycle would: (1) set the ref in effect-1,
  // (2) cancel effect-1's fetch via the cleanup, (3) effect-2 sees
  // the ref matches and returns early, (4) the cancelled fetch
  // never applies, and the brand stays on the default forever.
  const appliedSlug = useRef<string | null>(null);
  // Bumped when the signed-in user's organization or brand changed on the
  // server (seen by a token check - after a rename, when the tab regains
  // focus): the brand is fetched again even though the slug did not change.
  const [brandEpoch, setBrandEpoch] = useState(0);

  useEffect(() => {
    let seen = authedBrandSignature();
    const onAuthChange = () => {
      const next = authedBrandSignature();
      if (next === seen) return;
      seen = next;
      appliedSlug.current = null;
      setBrandEpoch(epoch => epoch + 1);
    };
    window.addEventListener(AUTH_CHANGE_EVENT, onAuthChange);
    return () => window.removeEventListener(AUTH_CHANGE_EVENT, onAuthChange);
  }, []);

  useEffect(() => {
    // The public documents are the platform's, in its own look on every
    // host — no tenant brand is fetched or painted for them.
    if (isPublicLegalPath(location.pathname)) return;
    let cancelled = false;
    const slug = readTenantFromPathname(location.pathname);

    // Same slug as last APPLIED brand — nothing to do (avoids
    // refetching on every in-tenant navigation).
    if (slug === appliedSlug.current) return;

    if (slug) {
      // STRICT TENANT ISOLATION: when the slug changes, apply the
      // NEW tenant's cached brand IMMEDIATELY (synchronous) so the
      // previous tenant's brand never bleeds into the new tenant's
      // pages. Then refresh from the API in the background.
      //
      // If no cache exists for this slug yet (first visit), we
      // intentionally do NOT call applyBrand(null) — that would
      // flash the default CorpBrain brand inside the customer's
      // site. The previous tenant's brand stays briefly until the
      // first API response arrives. Cold-start visit only.
      const cached = readCachedOverridesForSlug(slug);
      if (cached) {
        // Paint the cached brand for an instant render, but DON'T mark the
        // slug applied yet — the fresh fetch below must still run and win.
        // Marking here let StrictMode's mount→unmount→remount dedupe the
        // fetch away (effect-1 sets the ref + starts the fetch, the unmount
        // cancels that fetch, effect-2 sees the ref already matches and
        // returns early), leaving a STALE cache showing — one written
        // before newer brand fields (homeSemicircle, chat tint) existed, so
        // those treatments silently vanish on every navigate/refresh.
        applyBrand(cached);
      }
      resolveBrandFromTenantSlug(slug).then((result) => {
        if (cancelled) return;
        if (result.kind === 'fetch_failed') {
          // Backend hiccup, dev-server cold start, transient 5xx — do
          // NOT clobber the brand we (possibly) just painted from
          // cache. Wiping here is what made the sidebar / login logo
          // silently revert to the default CorpBrain look mid-session.
          return;
        }
        applyBrand(result.overrides);
        appliedSlug.current = slug;
      });
      // Cleanup: cancel the in-flight fetch so a stale slug-A
      // response can't paint over slug-B after the user navigates.
      return () => { cancelled = true; };
    }

    // URL has no tenant slug — reserved/root pages (/super-admin,
    // /design-system), /terms, /privacy, and localhost (host matches no
    // tenant domain). For an AUTHENTICATED user, FETCH their own tenant's
    // brand fresh (same endpoint + cache as slug URLs, keyed by the user's
    // tenant code). Fetching — not reusing the login-time session copy —
    // is what guarantees the brand always matches what the super-admin
    // just configured: the editor's save invalidates this endpoint's
    // cache, so the next paint (right after a change, and on every
    // refresh) is current.
    const authedCode = readAuthedTenantCode();
    if (authedCode) {
      resolveBrandFromTenantSlug(authedCode).then((result) => {
        if (cancelled) return;
        // Backend hiccup — keep the (sync-painted) brand, don't reset.
        if (result.kind === 'fetch_failed') return;
        applyBrand(result.overrides);
        appliedSlug.current = slug;
      });
      return () => { cancelled = true; };
    }

    // Not authenticated (e.g. pre-auth login on a vanity domain). DON'T
    // wipe the previously applied brand — transient redirect cycles yield
    // an empty slug mid-flight. Try to upgrade via host; only apply if the
    // host resolver returns something concrete.
    resolveBrandFromHost().then((overrides) => {
      if (cancelled) return;
      if (overrides) {
        applyBrand(overrides);
        appliedSlug.current = slug; // record empty-slug visit
      }
    });

    return () => { cancelled = true; };
  }, [location.pathname, brandEpoch]);

  return null;
}
